/**
 * Form Definition Store
 * Zustand store for Studio CRUD state — form definitions list, loading, saving, deleting.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { FormDefinition } from '@atlas-forms/types-js';

export interface FormDefinitionState {
  formDefinitions: FormDefinition[];
  selectedFormId: number | null;
  loading: boolean;
  saving: boolean;
  deleting: boolean;
  error: string | null;

  // Actions
  setFormDefinitions: (forms: FormDefinition[]) => void;
  addFormDefinition: (form: FormDefinition) => void;
  updateFormDefinition: (form: FormDefinition) => void;
  removeFormDefinition: (id: number) => void;
  selectForm: (id: number | null) => void;
  getSelectedForm: () => FormDefinition | undefined;
  setLoading: (loading: boolean) => void;
  setSaving: (saving: boolean) => void;
  setDeleting: (deleting: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

export const useFormDefinitionStore = create<FormDefinitionState>()(
  devtools(
    (set, get) => ({
      formDefinitions: [],
      selectedFormId: null,
      loading: false,
      saving: false,
      deleting: false,
      error: null,

      setFormDefinitions: (formDefinitions) => set({ formDefinitions }),

      addFormDefinition: (form) =>
        set((s) => ({ formDefinitions: [...s.formDefinitions, form] })),

      updateFormDefinition: (form) =>
        set((s) => ({
          formDefinitions: s.formDefinitions.map((f) =>
            f.FormID === form.FormID ? form : f
          ),
        })),

      removeFormDefinition: (id) =>
        set((s) => ({
          formDefinitions: s.formDefinitions.filter((f) => f.FormID !== id),
          selectedFormId: s.selectedFormId === id ? null : s.selectedFormId,
        })),

      selectForm: (selectedFormId) => set({ selectedFormId }),

      getSelectedForm: () => {
        const { formDefinitions, selectedFormId } = get();
        return formDefinitions.find((f) => f.FormID === selectedFormId);
      },

      setLoading: (loading) => set({ loading }),
      setSaving: (saving) => set({ saving }),
      setDeleting: (deleting) => set({ deleting }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
    }),
    { name: 'atlas-forms-form-definition-store' }
  )
);
